/**
 * The build pack (plan §3 step 5, §5, ui-spec §2.5): everything the Pack step and the A4 print
 * render, as data, read from the family-agnostic BoardAssembly: cut list grouped by material,
 * glue-ups, joints multiplied out, the family's construction rules marked self-authored.
 * Also the export commit rule: every export names a frozen revision.
 */
import type { ExportKind, ExportRecord, Fields, PackMetadata, Revision, Part } from '@/domain/schemas';
import type { ConstructionProfile } from '@/domain/profiles';
import { isProvisional, isSelfAuthored } from '@/domain/profiles';
import { fmtMm, fmtMm1, fmtNum, mm } from '@/domain/units';
import { shortHash } from '@/domain/hash';
import { changeLabel } from '@/domain/changes';
import type { Store } from '@/domain/store';
import type { StockUse } from '@/families/assembly';
import { elevationModel, elevationSvg, type ElevationModel } from './elevation';
import { stockLine } from './csv';
import { INVALID_STAMP, exportBlocker, formatPrinted, glueUpText, grainArrow, grainText, packFilename, packMetadata, watermarkFor, type ExportBuild } from './common';

export interface FastenerCounts {
  joints: number;
  perJoint: number;
  carcassScrews: number;
  backScrews: number;
  backCentresMm: number;
  wallBrackets: number;
}

const isBack = (id: string, parts: readonly Part[]) => parts.find((p) => p.id === id)?.role === 'back';
/** Parts of a door, drawer or sliding door (src/families/fronts.ts): fitted, not part of the carcass's screwed joints. */
const FRONT_ROLES = new Set(['door', 'door-stile', 'door-rail', 'glass', 'slider', 'drawer-front', 'drawer-side', 'drawer-rear', 'drawer-bottom']);
const isFront = (id: string, parts: readonly Part[]) => FRONT_ROLES.has(parts.find((p) => p.id === id)?.role ?? '');
const MOVING_KINDS = new Set(['hinge', 'runner', 'track']);

/**
 * Fastener quantities multiplied out from the joints (test-plan §2): carcass joints are every
 * joint between two carcass parts (one bay: 2(n + 2)); carcass screws = perJoint × joints;
 * back screws = Σ over the parts the back is fixed to of ⌊length/c⌋ + 1 (one bay:
 * 2(⌊H/c⌋ + 1) + (n + 2)(⌊(W − 2t)/c⌋ + 1)).
 */
export function fasteners(build: Pick<ExportBuild, 'parts' | 'joints' | 'construction'>): FastenerCounts {
  const c = build.construction;
  const parts = build.parts;
  const carcassJoints = build.joints.filter((j) => j.kind !== 'rebate' && j.kind !== 'egg-crate' && !MOVING_KINDS.has(j.kind) && !isBack(j.a, parts) && !isBack(j.b, parts) && !isFront(j.a, parts) && !isFront(j.b, parts));
  const joints = carcassJoints.length;
  const perJoint = c.fasteners.find((f) => f.qtyRule === 'per-joint')?.perJoint ?? 3;
  const backRule = c.fasteners.find((f) => f.qtyRule === 'back-perimeter-and-shelves');
  const centres = backRule?.centresMm ?? c.backFixing.spacingMm;
  let backScrews = 0;
  for (const j of build.joints) {
    const backId = isBack(j.a, parts) ? j.a : isBack(j.b, parts) ? j.b : null;
    if (!backId) continue;
    const other = parts.find((p) => p.id === (backId === j.a ? j.b : j.a));
    if (!other) continue;
    const len = other.grainWorld === 'y' ? other.placement.sy : other.placement.sx;
    backScrews += Math.floor(len / centres) + 1;
  }
  const brackets = c.fasteners.find((f) => f.qtyRule === 'fixed' && /bracket/i.test(f.id));
  return { joints, perJoint, carcassScrews: joints * perJoint, backScrews, backCentresMm: centres, wallBrackets: brackets?.qtyRule === 'fixed' ? brackets.qty : 0 };
}

const JOINT_NAMES: Record<string, [string, string]> = {
  'screwed-butt': ['screwed butt joint', 'screwed butt joints'],
  'housed-dado': ['housed dado', 'housed dadoes'],
  'dowel-butt': ['dowelled butt joint', 'dowelled butt joints'],
  rebate: ['rebate for the back', 'rebates for the back'],
  'pocket-screw': ['pocket-screwed joint', 'pocket-screwed joints'],
  'egg-crate': ['egg-crate halving', 'egg-crate halvings'],
};

/** "12 screwed butt joints · 8 housed dadoes, 6 mm deep" (carcass joints, back fixings excluded). */
export function jointsSummary(build: Pick<ExportBuild, 'parts' | 'joints'>): string {
  const counts = new Map<string, number>();
  for (const j of build.joints) {
    if (j.kind === 'screwed-butt' && (isBack(j.a, build.parts) || isBack(j.b, build.parts))) continue;
    // doors and drawers are listed with their hardware (packModel `fronts`), not as carcass joints
    if (MOVING_KINDS.has(j.kind) || isFront(j.a, build.parts) || isFront(j.b, build.parts)) continue;
    counts.set(j.kind, (counts.get(j.kind) ?? 0) + 1);
  }
  const t = build.parts.find((p) => p.role !== 'back')?.thicknessTicks ?? 0;
  return [...counts.entries()].map(([k, n]) => {
    const [one, many] = JOINT_NAMES[k] ?? [k, k];
    return `${n} ${n === 1 ? one : many}${k === 'housed-dado' ? `, ${fmtMm(Math.round(t / 3))} mm deep` : ''}`;
  }).join(' · ');
}

const HARDWARE_NAMES: Record<string, string> = {
  'cup-hinge': 'Concealed cup hinges', 'butt-hinge': 'Butt hinges', 'drawer-runner': 'Drawer runners (each)',
  'sliding-track': 'Sliding-door track (top and bottom)', knob: 'Knobs', 'bar-handle': 'Bar handles',
};

/** The doors-and-drawers section of the pack: hardware to buy, glass to order and the fitting notes; null without fronts. */
export function frontsSection(build: Pick<ExportBuild, 'parts' | 'hardware'>): PackModel['fronts'] {
  const fp = build.parts.filter((p) => FRONT_ROLES.has(p.role));
  if (!fp.length) return null;
  const doors = new Set(fp.filter((p) => p.role === 'door' || p.role === 'door-stile').map((p) => p.id.replace(/-stile-[lr]$/, ''))).size;
  const drawers = fp.filter((p) => p.role === 'drawer-front').length;
  const sliders = fp.filter((p) => p.role === 'slider').length;
  const glass = fp.filter((p) => p.role === 'glass');
  const hardware = (build.hardware ?? []).filter((h) => HARDWARE_NAMES[h.kind]).map((h) => ({ kind: HARDWARE_NAMES[h.kind]!, qty: h.qty, spec: h.spec ?? '' }));
  const fitting: string[] = [];
  if (hardware.some((h) => /cup/i.test(h.kind))) fitting.push('Bore each 35 mm hinge cup 4 mm from the door edge at the hinge positions; fit the mounting plates to the side and set the 2 mm reveal with the hinge cams.');
  if (hardware.some((h) => /Butt/.test(h.kind))) fitting.push('Recess the butt hinges flush in the door edge and the side; keep a 2.5 mm even gap all round the inset doors.');
  if (drawers) fitting.push('Fix the runners to the sides level with each drawer box\u2019s centre line, boxes first, then screw the fronts on from inside, adjusting for an even reveal.');
  if (sliders) fitting.push('Screw the track to the boards above and below the opening, then lift the doors into the top track and drop them into the bottom one.');
  const parts = [doors && `${doors} ${doors === 1 ? 'door' : 'doors'}`, drawers && `${drawers} ${drawers === 1 ? 'drawer' : 'drawers'}`, sliders && `${sliders / 2} pair${sliders === 2 ? '' : 's'} of sliding doors`].filter(Boolean);
  return {
    summary: parts.join(', '),
    hardware,
    glass: glass.map((g) => `${g.label}: ${fmtMm(g.finished.l)} × ${fmtMm(g.finished.w)} × ${fmtMm(g.finished.t)} mm, toughened`),
    fitting,
  };
}

/** Unloaded mass from the parts' finished volume and each stock's density. */
export function massKg(build: Pick<ExportBuild, 'parts' | 'stocks'>): number | null {
  let kg = 0;
  for (const p of build.parts) {
    const d = build.stocks.find((s) => s.id === p.stockId)?.density;
    if (!d) return null;
    kg += ((p.finished.l / 10) * (p.finished.w / 10) * (p.finished.t / 10) * p.qty / 1e9) * d;
  }
  return Math.round(kg * 10) / 10;
}

export interface PackContext {
  /** Design name; defaults to design.appearance.name. */
  name?: string | null;
  revisionId: string | null;
  /** The working state has edits since revisionId. */
  dirty?: boolean;
  fields?: Fields;
  generatedAt?: string;
}

export interface CutRow {
  id: string;
  label: string;
  role: Part['role'];
  qty: number;
  finishedMm: { l: number; w: number; t: number };
  cutMm: { l: number; w: number; t: number };
  sizeText: string;
  cutSizeText: string;
  grainText: string;
  grainArrow: string;
  edge: string;
  material: string;
  stockId: string;
  glueUp: string | null;
}

export interface CutGroup {
  stockId: string;
  kind: 'sheet' | 'board' | 'order';
  title: string;
  rows: CutRow[];
}

export interface PackModel {
  metadata: PackMetadata;
  title: string;
  familyLabel: string;
  overallText: string;
  overallMm: { W: number; H: number; D: number };
  shelves: number;
  panelCount: number;
  revisionText: string;
  hashShort: string;
  appearanceHashShort: string;
  stamp: { kind: 'draft' | 'invalid' | 'unconfirmed'; title: string; text: string };
  /** "Construction rules self-authored, not maker-reviewed" while self-authored, else null. */
  watermark: string | null;
  provisionalNote: string | null;
  band: string;
  header: { stockLine: string; profileLine: string; printedLine: string };
  elevation: ElevationModel;
  elevationSvg: string;
  datums: { shelfNo: number; partId: string; label: string; mm: number; text: string }[];
  sheetFit: { allFit: boolean; text: string; notes: string[] };
  cutRows: CutRow[];
  /** The cut list grouped by stock: sheet parts and solid boards (plan §3 step 5). */
  cutGroups: CutGroup[];
  /** "Shelf 2: 900 × 300 × 25, glued up from 2 strips of 150". */
  glueUps: string[];
  cutCaption: string;
  massText: string | null;
  profile: {
    id: string; version: string; name: string; provisional: boolean; selfAuthored: boolean; reviewer: string | null; reviewedAt: string | null;
    tag: string; note: string; rows: { label: string; text: string }[];
    assemblySteps: string[]; markingDrilling: string[]; installation: string[];
  };
  fasteners: FastenerCounts;
  /** Doors, drawers and sliding doors: what to buy and how to fit them (empty without fronts). */
  fronts: { summary: string; hardware: { kind: string; qty: number; spec: string }[]; glass: string[]; fitting: string[] } | null;
  errors: string[];
  warnings: string[];
  /** Why CSV and print are off, or null. */
  blocker: string | null;
  exports: { csv: { enabled: boolean; reason: string | null }; print: { enabled: boolean; reason: string | null }; json: { enabled: true; note: string | null } };
  footer: string;
}

function sheetText(s: StockUse): string {
  return s.sheet ? `${fmtMm(s.sheet.lengthTicks)} × ${fmtMm(s.sheet.widthTicks)}` : '';
}

/** The data the Pack step and the print pack render. `profiles.construction` defaults to the build's own. */
export function packModel(build: ExportBuild, project: PackContext, profiles: { construction?: ConstructionProfile } = {}): PackModel {
  const c = profiles.construction ?? build.construction;
  const generatedAt = project.generatedAt ?? new Date().toISOString();
  const metadata = packMetadata({ ...build, construction: c }, { revisionId: project.revisionId, generatedAt });
  const provisional = isProvisional(c);
  const selfAuthored = isSelfAuthored(c);
  const blocker = exportBlocker(build, project.fields ?? {});
  const revisionText = project.revisionId ? `${project.revisionId}${project.dirty ? ' + edits' : ''}` : 'Unsaved';
  const hashShort = shortHash(build.manufacturingHash);
  const firstError = build.validation.errors[0]?.message ?? '';
  const E = build.envelope;
  const parts = build.parts;
  const backPart = parts.find((p) => p.role === 'back');
  const shelfCount = parts.filter((p) => p.role === 'shelf').length;
  const dividers = parts.filter((p) => p.role === 'divider').length;
  const carcassStock = build.stocks[0];

  let stamp: PackModel['stamp'];
  if (!build.validation.ok) stamp = { kind: 'invalid', title: INVALID_STAMP, text: `${firstError} Exports and print are off until it is fixed.` };
  else if (blocker) stamp = { kind: 'unconfirmed', title: blocker.replace(/\.$/, ''), text: 'Exports are off until every critical value is confirmed.' };
  else stamp = { kind: 'draft', title: 'Draft', text: `Give this pack to your maker for revision ${project.revisionId ?? 'unsaved'} · ${hashShort}.` };

  const f = fasteners({ ...build, construction: c });
  const carcassFastener = c.fasteners.find((x) => x.qtyRule === 'per-joint');
  const backFastener = c.fasteners.find((x) => x.qtyRule === 'back-perimeter-and-shelves');
  const tol = c.tolerances;
  const where = dividers ? `(shelves, top and bottom into the sides and ${dividers} divider${dividers === 1 ? '' : 's'}; dividers into the top and bottom)` : `(each side into top, bottom and ${shelfCount} shelves)`;
  const rows = [
    { label: 'Joints', text: c.joint },
    { label: 'Joints, counted', text: jointsSummary(build) },
    { label: 'Fasteners', text: `${f.carcassScrews} × ${carcassFastener?.spec ?? 'screw'}: ${f.perJoint} per joint × ${f.joints} joints ${where}. ${carcassFastener?.use ?? ''}`.trim() },
    ...(backPart ? [{ label: 'Back (racking)', text: `About ${f.backScrews} × ${backFastener?.spec ?? 'screw'} at ${fmtNum(f.backCentresMm)} mm centres. ${c.backFixing.method} ${c.backFixing.rackingNote}` }] : [{ label: 'Back (racking)', text: 'No back: the wall fixing is the racking element. Fix to the wall before loading.' }]),
    { label: 'Wall fixing', text: `${c.wallFixing.required ? 'Required. ' : ''}${c.wallFixing.method} Hardware: ${c.wallFixing.hardware}.` },
    { label: 'Anti-tip', text: c.antiTip.method },
    { label: 'Edges', text: c.edgeTreatment },
    { label: 'Tolerances', text: `Panels ±${fmtNum(tol.panelLength)} mm · squareness ${tol.squareness} · shelf datums ±${fmtNum(tol.shelfDatum)} mm` },
    { label: 'Intended use', text: c.intendedUse },
    { label: 'Load', text: c.loadStatement },
  ];

  const allFit = build.stockFit.every((x) => x.fits);
  const sheets = build.stocks.filter((s) => s.kind === 'sheet' && s.sheet);
  const boards = build.stocks.filter((s) => s.kind === 'board');
  const sheetDesc = sheets[0] ? sheetText(sheets[0]) : '';
  const fitText = !allFit
    ? build.stockFit.filter((x) => !x.fits).map((x) => `${x.partId}: ${x.note}`).join(' ')
    : boards.length
      ? `Every solid part comes from one ${boards[0]!.material.toLowerCase().replace(/^solid /, '')} board or a listed glue-up${sheets.length ? `; the back fits one ${sheetDesc} sheet` : ''}.`
      : `Every part fits one ${sheetDesc} sheet with the grain along its length.`;
  const toRow = (p: Part): CutRow => ({
    id: p.id, label: p.label, role: p.role, qty: p.qty,
    finishedMm: { l: p.finished.l / 10, w: p.finished.w / 10, t: p.finished.t / 10 },
    cutMm: { l: p.cut.l / 10, w: p.cut.w / 10, t: p.cut.t / 10 },
    sizeText: `${fmtMm(p.finished.l)} × ${fmtMm(p.finished.w)} × ${fmtMm(p.finished.t)}`,
    cutSizeText: `${fmtMm(p.cut.l)} × ${fmtMm(p.cut.w)} × ${fmtMm(p.cut.t)}`,
    grainText: grainText(p), grainArrow: grainArrow(p), edge: p.edge, material: p.material, stockId: p.stockId,
    glueUp: p.glueUp ? `${p.glueUp.strips} strips of ${fmtMm(p.glueUp.stripWidthTicks)}` : null,
  });
  const cutRows = parts.map(toRow);
  const cutGroups: CutGroup[] = build.stocks
    .map((s) => ({ stockId: s.id, kind: s.kind, title: `${s.label}${s.kind === 'board' ? ' · solid boards' : s.kind === 'order' ? '' : ' · sheet'}`, rows: cutRows.filter((r) => r.stockId === s.id) }))
    .filter((g) => g.rows.length);
  const sameCut = parts.every((p) => p.cut.l === p.finished.l && p.cut.w === p.finished.w);
  const reviewedText = provisional ? null : `Reviewed by ${c.reviewer}, ${c.reviewedAt}`;
  const name = project.name ?? build.design.appearance?.name ?? 'Bookcase';
  const backMm = backPart ? fmtMm(backPart.thicknessTicks) : null;
  const mass = massKg(build);
  const watermark = watermarkFor(c);

  return {
    metadata,
    title: name || 'Bookcase',
    familyLabel: build.familyLabel,
    overallText: `${fmtNum(E.W)} × ${fmtNum(E.H)} × ${fmtNum(E.D)} mm (${backMm ? `depth includes the ${backMm} mm back` : 'no back'})`,
    overallMm: { W: E.W, H: E.H, D: E.D },
    shelves: shelfCount,
    panelCount: parts.length,
    revisionText,
    hashShort,
    appearanceHashShort: shortHash(build.appearanceHash),
    stamp,
    watermark,
    provisionalNote: !provisional ? null : selfAuthored
      ? 'The construction rules are self-authored and have not been reviewed by a maker. Sizes are exact; joints, fixings and loads are a starting point.'
      : 'The construction profile has not been reviewed by a maker. Sizes are exact; joints, fixings and loads are a starting point.',
    band: `${build.validation.ok ? 'DRAFT' : 'INVALID — NOT FOR CUTTING'} · revision ${revisionText} · ${hashShort}`,
    header: {
      stockLine: carcassStock ? `Stock ${carcassStock.id}: ${carcassStock.label}, measured ${fmtMm1(carcassStock.thicknessTicks)} mm` : `Stock ${stockLine(build)}`,
      profileLine: `Profile ${c.id} v${c.version} ${!provisional ? `(${reviewedText})` : selfAuthored ? '(self-authored)' : '(provisional)'}`,
      printedLine: `Printed ${formatPrinted(generatedAt)} · ${metadata.generatorVersion}`,
    },
    elevation: elevationModel(build),
    elevationSvg: elevationSvg(build, { interactive: false }),
    datums: build.datums.map((d, i) => ({ shelfNo: i + 1, partId: d.partId, label: d.label, mm: d.mm, text: `${fmtMm(mm(d.mm))} mm` })),
    sheetFit: { allFit, text: fitText, notes: ['Nesting onto sheets and boards is the maker’s call.'] },
    cutRows,
    cutGroups,
    glueUps: parts.map(glueUpText).filter((x): x is string => !!x),
    cutCaption: `L is always along the grain. ${sameCut ? 'Cut size equals finished size for this profile (panels cut to size on a panel saw).' : 'Cut sizes include the profile’s cut allowance.'}${boards.length ? ' Solid parts wider than the boards are edge-glued from the strips listed.' : ''} Select a row to find the part on the model.`,
    massText: mass ? `About ${fmtNum(Math.round(mass))} kg unloaded.${build.envelope.H > 1000 ? ' Fix it to the wall before loading (anti-tip).' : ''}` : null,
    profile: {
      id: c.id, version: c.version, name: c.name, provisional, selfAuthored, reviewer: c.reviewer, reviewedAt: c.reviewedAt,
      tag: !provisional ? reviewedText! : selfAuthored ? `Self-authored · v${c.version}` : `Provisional · v${c.version}`,
      note: !provisional ? 'Review is not structural certification.' : selfAuthored
        ? 'Construction rules are self-authored and not maker-reviewed. Review is not structural certification.'
        : 'Not yet reviewed by a maker. Review is not structural certification.',
      rows,
      assemblySteps: c.assemblySteps.slice(), markingDrilling: c.markingDrilling.slice(), installation: c.installation.slice(),
    },
    fasteners: f,
    fronts: frontsSection(build),
    errors: build.validation.errors.map((e) => e.message),
    warnings: build.validation.warnings.map((w) => w.message),
    blocker,
    exports: {
      csv: { enabled: !blocker, reason: blocker },
      print: { enabled: !blocker, reason: blocker },
      json: { enabled: true, note: build.validation.ok ? null : 'validation.ok is false' },
    },
    footer: `Review is not structural certification.${!provisional ? '' : selfAuthored ? ' The construction rules are self-authored: check them with your maker before cutting.' : ' The construction profile is provisional: check it with your maker before cutting.'}`,
  };
}

// ---------------------------------------------------------------------------
// Export commit rule
// ---------------------------------------------------------------------------

export type CommitForExportResult =
  | { ok: true; revision: Revision; saved: Revision | null; record: ExportRecord; filename: string }
  | { ok: false; reason: string };

/**
 * Every export names a frozen revision: unsaved edits are committed first (ensureRevision),
 * then an ExportRecord is appended to the store. CSV and print are refused while the design
 * is invalid or a critical value is unconfirmed; JSON is always allowed.
 */
export function commitForExport(store: Store, kind: ExportKind, { at = new Date().toISOString(), label }: { at?: string; label?: string } = {}): CommitForExportResult {
  const blocker = exportBlocker(store.build, store.fields);
  if (blocker && kind !== 'json') return { ok: false, reason: blocker };
  let saved: Revision | null = null;
  if (store.isDirty || !store.currentRevision) {
    saved = store.ensureRevision(label ?? changeLabel(store.currentRevision?.design, store.design, 'Exported'), { at });
  }
  const revision = store.currentRevision!;
  const build = store.build;
  const ext = kind === 'print' ? 'pdf' : kind;
  const filename = packFilename({ name: store.design.appearance?.name, revisionId: revision.id, manufacturingHash: build.manufacturingHash, ext });
  const record: ExportRecord = { at, kind, revisionId: revision.id, manufacturingHash: build.manufacturingHash, appearanceHash: build.appearanceHash, filename };
  store.recordExport(record);
  return { ok: true, revision, saved, record, filename };
}

/** The last export, if the design has changed since it ("Your design has changed since the last export"). */
export function lastExportChanged(store: Pick<Store, 'exports' | 'isDirty' | 'currentRevisionId' | 'build'>): ExportRecord | null {
  const last = store.exports[0];
  if (!last) return null;
  return store.isDirty || store.currentRevisionId !== last.revisionId || store.build.manufacturingHash !== last.manufacturingHash ? last : null;
}
